import React from 'react';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Grid, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import ColourAnimation from '../_assets/_lottie/colour.json';
import FadeAnimation from '../_assets/_lottie/fade.json';
import ScaleAnimation from '../_assets/_lottie/scale.json';
import SlideAnimation from '../_assets/_lottie/slide.json';

import Illustration from './_illustration';

const styles = cssMap({
	root: {
		// On mobile, each element is full width.
		gridTemplateColumns: '1fr',
		'@media (min-width: 30rem)': {
			// On larger viewports, they are laid out in two columns.
			gridTemplateColumns: '1fr 1fr',
		},
		paddingBlockStart: token('space.400'),
		paddingBlockEnd: token('space.400'),
	},
});

/**
 * Motion Properties
 *
 * This component displays a grid of motion properties with their corresponding labels and descriptions.
 * It uses the Illustration component to display the lottie animations.
 */
const MotionProperties = (): React.JSX.Element => {
	return (
		<Grid testId="grid-basic" rowGap="space.150" columnGap="space.150" xcss={styles.root}>
			<Stack space="space.100">
				<Illustration
					lottieFile={ScaleAnimation}
					size="small"
					hasSpacing={false}
					label="Two stacked rectangles scaling up to illustrate the scale property"
				/>
				<Heading size="small">Scale</Heading>
				<Text>Growing or shrinking in size.</Text>
			</Stack>
			<Stack space="space.100">
				<Illustration
					lottieFile={FadeAnimation}
					size="small"
					hasSpacing={false}
					label="Three rectangles fading from opaque to transparent to illustrate the fade property"
				/>
				<Heading size="small">Fade</Heading>
				<Text>Changing opacity.</Text>
			</Stack>
			<Stack space="space.100">
				<Illustration
					lottieFile={SlideAnimation}
					size="small"
					hasSpacing={false}
					label="A rectangle sliding in from the right edge of its container to the left edge to illustrate the slide property"
				/>
				<Heading size="small">Slide</Heading>
				<Text>Moving along the X or Y axis.</Text>
			</Stack>
			<Stack space="space.100">
				<Illustration
					lottieFile={ColourAnimation}
					size="small"
					hasSpacing={false}
					label="A rectangle changing color to illustrate the color property"
				/>
				<Heading size="small">Color</Heading>
				<Text>Transitioning between background or border colors.</Text>
			</Stack>
		</Grid>
	);
};

export default MotionProperties;
